/**
 * Global Connection Realtime Hook
 * 
 * Listens to connection_requests events via TWO RLS-compliant subscriptions:
 * - sender_id=eq.{userId} — outgoing request changes (accepts, declines)
 * - receiver_id=eq.{userId} — incoming request changes (new requests, cancellations)
 * 
 * Supabase Realtime enforces RLS, and the policy requires
 * auth.uid() = sender_id OR auth.uid() = receiver_id.
 * A single unfiltered subscription is denied; two filtered ones align with RLS.
 */

import { useCallback } from 'react';
import { useAuth } from './useAuth';
import { useRealtimeSubscription } from './useRealtimeSubscription';
import { useRealtimeInvalidations } from '@/lib/realtimeInvalidations';
import { getChannelName } from '@/lib/realtimeChannelNames';
import { logger } from '@/lib/logger';

/**
 * Global realtime subscription for all connection_requests events.
 * Uses two RLS-compliant channels (sender + receiver) to cover all relevant rows.
 */
export function useGlobalConnectionRealtime(): void {
  const { user } = useAuth();
  const invalidate = useRealtimeInvalidations();
  const currentUserId = user?.id;

  const handleEvent = useCallback(() => {
    if (!currentUserId) return;
    
    logger.debug('[GlobalConnectionRealtime] connection_requests event - invalidating');
    
    invalidate.connectionData();
  }, [currentUserId, invalidate]);

  // Subscription 1: rows where current user is the sender
  useRealtimeSubscription({
    channelName: getChannelName('connection_requests', 'user', `${currentUserId}-sender`),
    table: 'connection_requests',
    filter: currentUserId ? `sender_id=eq.${currentUserId}` : undefined,
    eventType: '*',
    onEvent: handleEvent,
    enabled: !!currentUserId,
  });

  // Subscription 2: rows where current user is the receiver
  useRealtimeSubscription({
    channelName: getChannelName('connection_requests', 'user', `${currentUserId}-receiver`),
    table: 'connection_requests',
    filter: currentUserId ? `receiver_id=eq.${currentUserId}` : undefined,
    eventType: '*',
    onEvent: handleEvent,
    enabled: !!currentUserId,
  });
}
